import toast from 'react-hot-toast';

/**
 * A utility function that shows loading toast while a promise is executing,
 * then automatically updates to success or error toast based on the result.
 *
 * @param promise The promise to wrap with toast notifications
 * @param loadingMessage Message to display during loading
 * @param successMessage Message to display on success
 * @param errorMessage Optional custom error message
 * @returns The promise result
 */
export const toastPromise = <T>(
  promise: Promise<T>,
  loadingMessage: string,
  successMessage: string,
  errorMessage?: string
): Promise<T> => {
  return toast.promise(
    promise,
    {
      loading: loadingMessage,
      success: data => {
        return typeof successMessage === 'function' ? successMessage(data) : successMessage;
      },
      error: err => {
        const message =
          typeof err === 'string' ? err : err?.message || errorMessage || 'An error occurred';
        return message;
      },
    },
    {
      style: {
        minWidth: '200px',
      },
      success: {
        duration: 3000,
        icon: '✅',
      },
      error: {
        duration: 4000,
        icon: '❌',
      },
    }
  );
};

/**
 * Enhanced version of toastPromise that allows for customizing the toast component
 * and provides a more detailed API for managing promises with toast notifications.
 */
export const withToast = <T>(
  asyncFn: () => Promise<T>,
  options: {
    loading: string;
    success: string | ((data: T) => string);
    error?: string | ((error: any) => string);
    successDuration?: number;
    errorDuration?: number;
  }
) => {
  const toastId = toast.loading(options.loading, {
    style: {
      minWidth: '250px',
      backgroundColor: '#fff',
      borderLeft: '4px solid #60a5fa', // blue-400
      padding: '16px',
    },
  });

  return asyncFn()
    .then(data => {
      const message =
        typeof options.success === 'function' ? options.success(data) : options.success;

      toast.success(message, {
        id: toastId,
        duration: options.successDuration || 3000,
        style: {
          minWidth: '250px',
          backgroundColor: '#f0fdf4', // green-50
          borderLeft: '4px solid #22c55e', // green-500
          padding: '16px',
        },
      });
      return data;
    })
    .catch(error => {
      const errorMsg =
        typeof options.error === 'function'
          ? options.error(error)
          : options.error ||
            (typeof error === 'string' ? error : error?.message || 'An error occurred');

      toast.error(errorMsg, {
        id: toastId,
        duration: options.errorDuration || 4000,
        style: {
          minWidth: '250px',
          backgroundColor: '#fef2f2', // red-50
          borderLeft: '4px solid #ef4444', // red-500
          padding: '16px',
        },
      });
      throw error; // Re-throw error to allow proper error handling downstream
    });
};
